import React, {useEffect, useState, useRef} from "react";
import {useNavigate} from "react-router-dom";
import {useSelector} from "react-redux";
import moment from "moment";

import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import CardsContainer from "@/components/CardsContainer";
import CardsList from "@/components/CardsList";
import CardsTable from "@/components/CardsTable";

import AddIcon from "@mui/icons-material/Add";
import RateReviewIcon from "@mui/icons-material/RateReview";
import BadgeIcon from "@mui/icons-material/Badge";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

import EnrollPlayerModal from "@/components/EnrollPlayerModal";
import ClubCrest from "@/components/ClubCrest";
import StatusBadge from "@/components/StatusBadge";

import Alert from "@mui/material/Alert";
import {PlayerRoleAPI} from "@/api/player_role";
import {
	getDsContext,
	readDsPreference,
	writeDsPreference,
} from "@/utils/dsContext";
import {useScoutingPipeline} from "@/utils/scoutingPipeline";
import {PlayerAPI} from "@/api/player";

import {ERROR_OPERATION, AREA_OPTIONS} from "@/utils/constants";
import {getBirthYears} from "@/utils/functions";
import {useTranslation} from "react-i18next";

const ITEMS_PER_PAGE = 24;

// Stato del giocatore osservato: pallino colorato con il testo
const STATUS_CONFIG = {
	reported: {label: "Da provare", tone: "neutral"},
	valuated: {label: "Valutato", tone: "warning"},
	discarted: {label: "Scartato", tone: "neutral"},
	buyed: {label: "Acquistato", tone: "good"},
};

function ScoutingDatabasePage(props) {
	const {handleNotification} = props;

	const navigate = useNavigate();
	const seasonId = useSelector((state) => state?.season?.id);
	const {t} = useTranslation();

	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);
	const [optionsError, setOptionsError] = useState(false);
	const [options, setOptions] = useState({});
	const [roles, setRoles] = useState([]);
	const requestRevision = useRef(0);
	const context = getDsContext();
	useScoutingPipeline();
	const [players, setPlayers] = useState([]);
	const [totalItems, setTotalItems] = useState();
	const [currentPage, setCurrentPage] = useState(1);
	const [currentSorting, setCurrentSorting] = useState();
	const [currentSortingOrder, setCurrentSortingOrder] = useState();

	const [enrollPlayer, setEnrollPlayer] = useState(null);

	const [filtersData, setFiltersData] = useState(
		readDsPreference("scouting_database_filters", {}),
	);

	const updateFilter = (key, value) => {
		const newFiltersData = {...filtersData, [key]: value};
		if (key === "competitionSeasonId") newFiltersData.groupId = null;
		writeDsPreference("scouting_database_filters", newFiltersData);
		setFiltersData(newFiltersData);
		setCurrentPage(1);
	};

	const statusOptions = [
		{value: "reported", label: "Da provare"},
		{value: "valuated", label: "Valutato"},
		{value: "discarted", label: "Scartato"},
		{value: "buyed", label: "Acquistato"},
	];

	const selectFilter = (key, label, items) => ({
		key,
		label,
		type: "select",
		options: items,
		sorted: false,
		value: filtersData[key],
		setValue: (value) => updateFilter(key, value),
	});
	const bools = [
		{value: true, label: "Sì"},
		{value: false, label: "No"},
	];
	const filters = [
		{
			key: "search",
			label: "Giocatore o squadra",
			value: filtersData?.search || "",
			setValue: (value) => updateFilter("search", value),
		},
		{
			key: "birthYear",
			label: "Anno di nascita",
			type: "select",
			options: getBirthYears(),
			sorted: false,
			value: filtersData?.birthYear,
			setValue: (value) => updateFilter("birthYear", value),
		},
		{
			key: "role",
			label: "Ruolo",
			type: "select",
			options: AREA_OPTIONS,
			sorted: false,
			value: filtersData?.role,
			setValue: (value) => updateFilter("role", value),
		},
		{
			key: "prospectStatus",
			label: "Status",
			type: "select",
			options: statusOptions,
			sorted: false,
			value: filtersData?.prospectStatus,
			setValue: (value) => updateFilter("prospectStatus", value),
		},
		selectFilter(
			"clubId",
			"Squadra",
			(options.clubs || []).map((o) => ({value: o.id, label: o.name})),
		),
		selectFilter(
			"competitionSeasonId",
			"Competizione",
			(options.competitions || []).map((o) => ({value: o.id, label: o.name})),
		),
		selectFilter(
			"groupId",
			"Girone",
			(options.groups || [])
				.filter(
					(g) =>
						!filtersData.competitionSeasonId ||
						g.competitionSeasonId === filtersData.competitionSeasonId.value,
				)
				.map((o) => ({value: o.id, label: o.name})),
		),
		selectFilter(
			"internalRoleId",
			"Ruolo interno",
			roles
				.filter((r) => r.active !== false)
				.map((r) => ({
					value: r.id,
					label: r.code ? `${r.code} · ${r.name}` : r.name,
				})),
		),
		selectFilter("department", "Reparto", [
			{value: "P", label: "Portieri"},
			{value: "D", label: "Difensori"},
			{value: "C", label: "Centrocampisti"},
			{value: "A", label: "Attaccanti"},
		]),
		selectFilter("birthYearFrom", "Anno da", getBirthYears()),
		selectFilter("birthYearTo", "Anno a", getBirthYears()),
		selectFilter(
			"favoriteFoot",
			"Piede",
			(options.favoriteFeet || []).map((f) => ({value: f, label: f})),
		),
		selectFilter(
			"attitude",
			"Attitudine",
			["D", "R", "AR", "AF"].map((a) => ({value: a, label: a})),
		),
		selectFilter("monitored", "Monitorato", bools),
		selectFilter("hasReport", "Con relazione", bools),
		selectFilter("inSeason", "Tesseramento nella stagione", [
			{value: true, label: "Presente"},
			{value: false, label: "Solo stagioni precedenti"},
		]),
	];

	const loadOptions = async () => {
		setOptionsError(false);
		try {
			const [catalog, roleRows] = await Promise.all([
				PlayerAPI.scoutingFilters({seasonId}),
				PlayerRoleAPI.list(),
			]);
			if (context !== getDsContext()) return;
			setOptions(catalog || {});
			setRoles(roleRows || []);
		} catch {
			if (context === getDsContext()) setOptionsError(true);
		}
	};
	useEffect(() => {
		loadOptions();
	}, [seasonId]); // eslint-disable-line react-hooks/exhaustive-deps
	const getPlayers = async () => {
		const revision = ++requestRevision.current;
		setLoading(true);
		setError(false);
		setPlayers([]);
		setTotalItems(undefined);
		try {
			const query = Object.fromEntries(
				Object.entries(filtersData).map(([key, value]) => [
					key,
					key === "role" ? value?.label : value?.value ?? value ?? undefined,
				]),
			);
			const response = await PlayerAPI.paginated({
				page: currentPage,
				pageSize: ITEMS_PER_PAGE,
				prospect: true,
				sorting: currentSorting,
				descending: currentSortingOrder === "descend",
				...query,
				seasonId,
			});
			if (revision !== requestRevision.current || context !== getDsContext())
				return;
			setPlayers(response?.data || []);
			setTotalItems(response?.totalRecords ?? 0);
		} catch {
			if (revision === requestRevision.current && context === getDsContext()) {
				setError(true);
				handleNotification(ERROR_OPERATION, "error");
			}
		} finally {
			if (revision === requestRevision.current && context === getDsContext())
				setLoading(false);
		}
	};
	useEffect(() => {
		requestRevision.current += 1;
		const timer = setTimeout(getPlayers, 500);
		return () => {
			clearTimeout(timer);
			requestRevision.current += 1;
		};
	}, [filtersData, currentPage, currentSorting, currentSortingOrder, seasonId]); // eslint-disable-line react-hooks/exhaustive-deps

	const handlePageChanged = (currentPage) => {
		setCurrentPage(currentPage);
	};

	const fromOptionToSorting = (sorting) => {
		switch (sorting) {
			case "firstName":
				return 2;
			case "lastName":
				return 1;
			case "internalRole":
				return 4;
			case "memberships":
				return 5;
			case "dateOfBirth":
				return 3;
			default:
				return;
		}
	};

	const filteredPlayers = Array.from(players);

	const listConfiguration = {
		data: filteredPlayers,
		onClick: (player) => navigate("/scouting/players/" + player.id),
	};

	const columns = [
		{label: "Cognome", key: "lastName", align: "start", sortable: true},
		{label: t("name"), key: "firstName", align: "start", sortable: true},
		{
			label: "Anno",
			key: "dateOfBirth",
			align: "center",
			small: true,
			sortable: true,
			customRender: (item) => {
				if (!item.dateOfBirth) return "---";
				const year = moment(item.dateOfBirth).year();
				return year > 1900 ? year : "---";
			},
		},
		{
			label: "Età",
			key: "age",
			align: "center",
			small: true,
			customRender: (item) => {
				if (!item.dateOfBirth) return "---";
				const year = moment(item.dateOfBirth).year();
				if (year <= 1900) return "---";
				return moment().diff(moment(item.dateOfBirth), "years");
			},
		},
		{
			label: "Ruolo",
			key: "internalRole",
			sortable: true,
			align: "start",
			medium: true,
			customRender: (row) => {
				return (
					row.internalRole?.code ||
					(row.goalkeeper
						? "Portiere"
						: row.secondaryRole || row.primaryRole || "---")
				);
			},
		},
		{
			label: "Squadra",
			key: "memberships",
			align: "start",
			medium: true,
			sortable: true,
			customRender: (row) =>
				(row.memberships || []).length
					? row.memberships.map((r) => (
							<span key={r.id} className="club-crest-label">
								<ClubCrest
									name={r.club?.name}
									logoUrl={r.club?.imageUrl}
									colors={r.club?.colors}
									size={16}
								/>
								{r.club?.name || r.name}
							</span>
						))
					: "—",
		},
		{
			label: "Media relazioni",
			key: "reportsAverage",
			align: "center",
			small: true,
			customRender: (row) =>
				!row.scoutingReportsAvailable
					? "Non disponibile"
					: row.reportsAverage == null
						? "—"
						: `${row.reportsAverage.toFixed(2)} ${{up: "↑", down: "↓", flat: "→"}[row.reportsTrend] || ""}`,
		},
		{
			label: "Media storica",
			key: "valuationsAverage",
			align: "center",
			small: true,
			customRender: (row) =>
				row.valuationsAverage == null
					? "—"
					: `${row.valuationsAverage.toFixed(2)} ${{up: "↑", down: "↓", flat: "→"}[row.valuationsTrend] || ""}`,
		},
		{
			label: "Stato",
			key: "prospectStatus",
			align: "center",
			medium: true,
			customRender: (row) => {
				const cfg = STATUS_CONFIG[row.prospectStatus];
				if (!cfg) return "—";
				return <StatusBadge tone={cfg.tone} label={cfg.label} />;
			},
		},
		{
			label: "",
			key: "newValuation",
			align: "end",
			small: true,
			customRender: (player) => (
				<Tooltip title="Nuova relazione">
					<IconButton
						size="small"
						color="primary"
						aria-label="Nuova relazione"
						onClick={(event) => {
							event.stopPropagation();
							navigate("/scouting/relazioni/new?playerId=" + player.id);
						}}
					>
						<RateReviewIcon fontSize="small" />
					</IconButton>
				</Tooltip>
			),
		},
		{
			label: "",
			key: "enroll",
			align: "center",
			small: true,
			customRender: (row) => (
				<Tooltip title="Tessera">
					<IconButton
						size="small"
						color="success"
						onClick={(e) => {
							e.stopPropagation();
							setEnrollPlayer(row);
						}}
					>
						<BadgeIcon fontSize="small" />
					</IconButton>
				</Tooltip>
			),
		},
	];

	const sortableColumns = columns.filter((c) => c.sortable);

	const headerActions = [
		{
			icon: <AddIcon />,
			title: "Aggiungi",
			onClick: () => navigate("/scouting/players/new"),
		},
	];

	return (
		<Page>
			<Loading visible={loading} />
			<Breadcrumbs
				current="Giocatori osservati"
				actions={<MainActions actions={headerActions} />}
			/>
			{error && (
				<Alert severity="error">
					Elenco non disponibile.{" "}
					<MainActions
						actions={[{label: "Riprova elenco", onClick: getPlayers}]}
					/>
				</Alert>
			)}
			{optionsError && (
				<Alert severity="warning">
					Opzioni dei filtri non disponibili.{" "}
					<MainActions
						actions={[{label: "Riprova filtri", onClick: loadOptions}]}
					/>
				</Alert>
			)}
			<CardsContainer
				tableComponent={
					<CardsTable
						fullWidth
						breadcrumbs
						filtersData={filters}
						sortOptions={sortableColumns}
						configurationColumns={columns}
						data={filteredPlayers}
						handleClick={(player) => navigate("/scouting/players/" + player.id)}
						handleSortChange={(option, order) => {
							setCurrentSorting(fromOptionToSorting(option));
							setCurrentSortingOrder(order);
						}}
						totalItems={totalItems}
						itemsPerPage={ITEMS_PER_PAGE}
						handlePageChanged={handlePageChanged}
						page={currentPage}
					/>
				}
				listComponent={
					<CardsList
						type="scouting_database"
						breadcrumbs
						filtersData={filters}
						sortOptions={sortableColumns}
						listConfiguration={listConfiguration}
						handleSortChange={(option, order) => {
							setCurrentSorting(fromOptionToSorting(option));
							setCurrentSortingOrder(order);
						}}
						totalItems={totalItems}
						itemsPerPage={ITEMS_PER_PAGE}
						handlePageChanged={handlePageChanged}
						page={currentPage}
					/>
				}
			/>
			<EnrollPlayerModal
				open={!!enrollPlayer}
				close={() => setEnrollPlayer(null)}
				playerId={enrollPlayer?.id}
				playerData={enrollPlayer}
				onSuccess={() => getPlayers()}
				handleNotification={handleNotification}
			/>
		</Page>
	);
}

export default function ScoutingDatabaseContext(props) {
	useSelector((s) => s.user);
	useSelector((s) => s.token);
	useSelector((s) => s.season?.id);
	return <ScoutingDatabasePage key={getDsContext()} {...props} />;
}
